import { Dialog, Box } from "@mui/material";
import Select from "react-select";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip, IconButton } from "@mui/material";

const ReportSubscriptionModal = ({
  selectedAsset,
  subscriptionForm,
  closeSubsciptionModal,
  formErrors,
  formTouched,
  allFieldsValid,
  handleSubscriptionSubmit,
  handleSubscriptionChange,
  setShowSubscriptionForm,
  showSubscriptionForm,
  products,
}) => {
  return (
    <Dialog
      open={showSubscriptionForm}
      onClose={closeSubsciptionModal}
      maxWidth="lg"
      fullWidth
    >
      <Box p={3}>
        <div className="text-lg font-bold mb-4">
          {" "}
          Subscribe to {selectedAsset.name}
          <Tooltip
            arrow
            placement="right"
            title={
              <>
                <strong style={{ fontSize: "1rem" }}>How this works</strong>
                <br />
                Search for your project or product by name. If it has already
                been provisioned on Azure or GCP and registered in DataPulse, it
                will automatically appear in the search results.
              </>
            }
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: "0.95rem",
                  lineHeight: 1.5,
                  maxWidth: 320,
                },
              },
            }}
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </div>
        <form>
          <div className="form-grid">
            <div className="grid [grid-template-columns:1fr_1fr] gap-2 p-2">
              <div className="mb-3">
                <label className="text-[14px] font-bold">
                  Product Name (Project on Azure/GCP){" "}
                  <span className="text-red-500">*</span>
                </label>
                <Select
                  name="productName"
                  value={
                    subscriptionForm.productName
                      ? {
                          value: subscriptionForm.productName,
                          label: subscriptionForm.productName,
                        }
                      : null
                  }
                  onChange={(selectedOption) => {
                    handleSubscriptionChange({
                      target: {
                        name: "productName",
                        value: selectedOption ? selectedOption.value : "",
                        readerGroup: selectedOption?.rg_name,
                        source: selectedOption?.source,
                      },
                    });
                  }}
                  options={
                    products?.map((p) => ({
                      ...p,
                      value: p.product_name,
                      label: `${p.product_name} (${
                        p.source === "TERRAFORM" ? "NOT REGISTERED" : p.source
                      })`,
                      searchText: `${p.product_name} ${
                        p.search_text || ""
                      }`.toLowerCase(),
                    })) || []
                  }
                  placeholder="Search a product"
                  isClearable
                  isSearchable
                  filterOption={(option, inputValue) =>
                    option.data.searchText.includes(inputValue.toLowerCase())
                  }
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? "#FF03F3" : "#e5e7eb",
                      borderRadius: "0.25rem",
                      padding: "2px",
                      boxShadow: state.isFocused ? "0 0 0 1px #FF03F3" : "none",
                      "&:hover": {
                        borderColor: state.isFocused ? "#FF03F3" : "#cbd5e1",
                      },
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: "#9ca3af",
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? "rgba(0, 0, 160, 0.1)"
                        : state.isFocused
                        ? "rgba(0, 0, 160, 0.05)"
                        : "white",
                      color: state.isSelected ? "#FF03F3" : "#374151",
                      "&:active": {
                        backgroundColor: "rgba(0, 0, 160, 0.15)",
                      },
                    }),
                  }}
                  noOptionsMessage={() => "No products found"}
                />
                {formTouched && formErrors.productName && (
                  <div className="text-red-500 text-xs">
                    {formErrors.productName}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="text-[14px] font-bold">
                  Reader Group (Auto populates){" "}
                  <span className="text-red-500">*</span>
                  <Tooltip
                    arrow
                    placement="right"
                    title={
                      "Reader groups are auto-populated. If you choose to edit them, please ensure the values are correct; otherwise, access will not be granted."
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: "0.95rem",
                          lineHeight: 1.5,
                          maxWidth: 320,
                        },
                      },
                    }}
                  >
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                </label>
                <input
                  name="readerGroup"
                  value={subscriptionForm.readerGroup}
                  onChange={handleSubscriptionChange}
                  placeholder="Enter Reader Group"
                  className="w-full border rounded p-2 focus:outline focus:outline-2 focus:outline-[#FF03F3]  h-[42px]"
                />

                {subscriptionForm.readerGroup && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {subscriptionForm.readerGroup
                      .split("|")
                      .map((group, index) => (
                        <span
                          key={index}
                          className="bg-[#afaae9] text-white text-xs px-3 py-1 rounded-full"
                        >
                          {group}
                        </span>
                      ))}
                  </div>
                )}
                {formTouched && formErrors.readerGroup && (
                  <div className="text-red-500 text-xs">
                    {formErrors.readerGroup}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="text-[14px] font-bold">
                  Usage Duration <span className="text-red-500">*</span>
                </label>
                <select
                  name="usageDuration"
                  value={subscriptionForm.usageDuration}
                  onChange={handleSubscriptionChange}
                  className="w-full border rounded p-2 focus:outline focus:outline-2 focus:outline-[#FF03F3]  h-[42px]"
                >
                  <option value="">Select Usage Duration</option>
                  <option value="3months">3 Months</option>
                  <option value="6months">6 Months</option>
                  <option value="1year">1 Year</option>
                  <option value="lifetime">Lifetime</option>
                </select>
                {formTouched && formErrors.usageDuration && (
                  <div className="text-red-500 text-xs">
                    {formErrors.usageDuration}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="text-[14px] font-bold">
                  Usage Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="textarea"
                  name="usageDescription"
                  value={subscriptionForm.usageDescription}
                  onChange={handleSubscriptionChange}
                  placeholder="Describe how you plan to use this data"
                  className="w-full border rounded p-2 focus:outline focus:outline-2 focus:outline-[#FF03F3]  h-[42px] "
                />
                {formTouched && formErrors.usageDescription && (
                  <div className="text-red-500 text-xs">
                    {formErrors.usageDescription}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="text-[14px] font-bold">
                  Subscribe Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="notificationEmail"
                  value={subscriptionForm.notificationEmail}
                  onChange={handleSubscriptionChange}
                  placeholder="Enter email (@effem.com)"
                  className="w-full border rounded p-2 
             focus:outline focus:outline-2 focus:outline-[#FF03F3]
             outline-offset-0 h-[42px]"
                />

                {formTouched && formErrors.notificationEmail && (
                  <div className="text-red-500 text-xs">
                    {formErrors.notificationEmail}
                  </div>
                )}
                {!formTouched &&
                  subscriptionForm.notificationEmail &&
                  formErrors.notificationEmail && (
                    <div className="text-red-500 text-xs mt-1">
                      {formErrors.notificationEmail}
                    </div>
                  )}
              </div>
            </div>
            <div className="form-consent">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={subscriptionForm.acceptTerms}
                onChange={handleSubscriptionChange}
              />
              <label>
                I agree to the data contract terms and will use the data in
                accordance with the specified quality requirements and usage
                guidelines.
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={closeSubsciptionModal}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubscriptionSubmit}
                className={`px-4 py-2 rounded ${
                  !subscriptionForm.acceptTerms || !allFieldsValid
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#FF03F3] text-white"
                } `}
                disabled={!subscriptionForm.acceptTerms || !allFieldsValid}
              >
                Subscribe
              </button>
            </div>
          </div>
        </form>
      </Box>
    </Dialog>
  );
};

export default ReportSubscriptionModal;
